import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { films } from "@/mocks/films";
import { filmCategoryMeta } from "@/mocks/categories";
import { useSEO } from "@/hooks/useSEO";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import FilmCard from "@/components/base/FilmCard";
import FilmGallery from "@/pages/detail/components/FilmGallery";
import VideoBox from "@/pages/detail/components/VideoBox";

export default function FilmDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [copied, setCopied] = useState(false);

  const film = films.find((item) => item.id === id);
  const meta = film ? filmCategoryMeta[film.category] : null;

  useSEO({
    title: film ? `${film.title} - 影视详情 - 星光传媒` : "作品未找到 - 星光传媒",
    description: film
      ? `${film.title}：${film.synopsis.slice(0, 70)}…由${film.director}执导，${film.cast.join("、")}主演，${film.year}年上映。`
      : "抱歉，未找到对应的影视作品。",
    keywords: film
      ? `${film.title},星光传媒,${film.genres.join(",")},影视作品详情`
      : "星光传媒,影视作品",
  });

  if (!film || !meta) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-file-unknow-line"
          title="没有找到这部作品"
          desc="你访问的作品可能已被移除或链接有误，去看看其他精彩内容吧。"
          action={
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                返回首页
              </Link>
              <Link
                to="/category/movie"
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
              >
                浏览电影
              </Link>
            </div>
          }
        />
      </div>
    );
  }

  const active = isFavorite(film.id);
  const related = films
    .filter((item) => item.category === film.category && item.id !== film.id)
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, 4);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const metaRows = [
    { label: "导演", value: film.director },
    { label: "主演", value: film.cast.join(" / ") },
    { label: "类型", value: film.genres.join(" · ") },
    { label: "上映年份", value: `${film.year} 年` },
    { label: "地区", value: film.region },
    { label: "制作公司", value: film.company },
  ];

  return (
    <article>
      {/* Hero band */}
      <section className="relative overflow-hidden bg-foreground-950">
        <img
          src={film.videoPoster}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/85 to-foreground-950/70" />
        <div className="relative mx-auto w-full max-w-[1280px] px-4 pb-8 pt-6 md:px-6 md:pb-12 md:pt-8">
          <Breadcrumb
            tone="light"
            items={[
              { label: "首页", to: "/" },
              { label: `${meta.label}分类`, to: `/category/${film.category}` },
              { label: film.title },
            ]}
          />
          <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="w-40 shrink-0 overflow-hidden rounded-lg border border-background-50/10 md:w-56">
              <img
                src={film.cover}
                alt={`${film.title}海报`}
                title={`${film.title} - ${meta.label} - 星光传媒`}
                className="aspect-[2/3] w-full object-cover object-top"
              />
            </div>
            <div className="min-w-0 flex-1">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/20 px-3 py-1 text-xs font-medium text-accent-300">
                {meta.label}
              </span>
              <h1 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-4xl">
                {film.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-sm font-semibold text-background-50">
                  <i className="ri-star-fill text-xs" />
                  {film.rating.toFixed(1)}
                </span>
                <div className="flex flex-wrap gap-2">
                  {film.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-background-50/10 px-2.5 py-1 text-xs text-background-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-16 shrink-0 text-background-400">{row.label}</dt>
                    <dd className="text-background-100">{row.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(film.id)}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                    active
                      ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                      : "border border-background-50/30 text-background-50 hover:bg-background-50/10"
                  }`}
                >
                  <i className={active ? "ri-heart-fill text-base" : "ri-heart-line text-base"} />
                  {active ? "已收藏" : "收藏作品"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-share-line text-base" />
                  {copied ? "链接已复制" : "分享作品"}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/category/${film.category}`)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-arrow-left-line text-base" />
                  返回{meta.label}分类
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-col gap-10 lg:flex-row lg:gap-12">
          <div className="min-w-0 flex-1">
            <section>
              <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                剧情简介
              </h2>
              <p className="text-sm leading-7 text-foreground-700 md:text-base">
                {film.synopsis}
              </p>
            </section>

            <section className="mt-10">
              <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                剧照欣赏
              </h2>
              <FilmGallery stills={film.stills} title={film.title} />
            </section>

            <section className="mt-10">
              <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                视频预告
              </h2>
              <VideoBox poster={film.videoPoster} title={film.title} />
            </section>
          </div>

          <aside className="w-full shrink-0 lg:w-72">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="mb-4 font-heading text-base font-semibold text-foreground-950">
                作品信息
              </h3>
              <ul className="flex flex-col gap-3 text-sm">
                {metaRows.map((row) => (
                  <li key={row.label} className="flex justify-between gap-3">
                    <span className="text-foreground-500">{row.label}</span>
                    <span className="text-right text-foreground-900">{row.value}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => toggleFavorite(film.id)}
                className={`mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                  active
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={active ? "ri-heart-fill" : "ri-heart-line"} />
                {active ? "取消收藏" : "加入收藏"}
              </button>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14">
            <h2 className="mb-6 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
              {related.map((item) => (
                <FilmCard key={item.id} film={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}